import apiEndPoints from "../../apiconstants";
import { api, deafultResponseAPIResponse, handleErrorResponse } from "../api";
import {
  GetDetailsAPIResponse,
  SettingsData,
  SettingsTeamsAPIResponse,
  Schedule,
  CreateSchedulePayload,
  UpdateSchedulePayload,
  SchedulerHistoryResponse,
  SchedulerBatchDetailResponse,
  Credential,
  CredentialPayload,
  TenantMetadataData,
  GetTenantMetadataAPIResponse,
  TenantMetadataPatchPayload,
  TenantMetadataPatchResponse,
  TenantMetadataUploadRequest,
  TenantMetadataUploadResponse,
  GetTenantMetadataUploadAPIResponse,
  GetTenantTimezonesAPIResponse,
  TenantTimezone,
  AuditLogsData,
  AuditLogsAPIResponse,
  AuditLogDetail,
  AuditLogDetailAPIResponse,
  AuditLogsHistoryRequest,
  DataConnectorsListPayload,
  DataConnector,
  CreateDataConnectorPayload,
  UpdateDataConnectorPayload,
  GetDataConnectorsQueryArg,
  ReprocessClaimsPayload,
  ReprocessClaimsAPIResponse,
} from "./settingstypes";

export interface TeamMemberPayLoad {
  invitees: {
    invitee_email: string,
    invitee_metadata: {
      role: string,
      name: string
    };
  }[]
}


export interface GetDataPayload {
  db_name: string;
  table: string;
  fields: string[];
  filters: Record<string, any>;
}

export interface UpdateDataPayload {
  db_name: string;
  table: string;
  field: 'bot_data' | 'workflow_data';
  value: Record<string, any>;
  filters: Record<string, any>;
}




export const settingsApiSlice = api.injectEndpoints({
  endpoints: build => ({
    inviteUser: build.mutation<deafultResponseAPIResponse, TeamMemberPayLoad>({
      query: (payload) => ({
        url: apiEndPoints?.inviteUser,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getTeamMembers: build.query<SettingsTeamsAPIResponse, void>({
      query: () => apiEndPoints?.getTeamMembers,
      transformResponse: (response: any) => response?.data?.response || [],
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error) => [{ type: "Settings" }],
    }),
    getDetails: build.query<GetDetailsAPIResponse, void>({
      query: () => apiEndPoints?.getDetails,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error) => [{ type: "Settings" }]
    }),
    updateTenantDetails: build.mutation<deafultResponseAPIResponse, { meta_data: Record<string, unknown>; default_timezone: string }>({
      query: (payload) => ({
        url: apiEndPoints?.getDetails+'/metadata',
        method: 'PATCH',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error) => [{ type: "Settings" }, { type: "Settings", id: "TenantMetadata" }],
    }),
    getTenantMetadata: build.query<TenantMetadataData, void>({
      query: () => apiEndPoints?.getTenantMetadata,
      transformResponse: (response: GetTenantMetadataAPIResponse): TenantMetadataData => {
        if (response?.data && typeof response.data === 'object') {
          return {
            widget_icon_url: response.data.widget_icon_url ?? '',
            font_url: response.data.font_url ?? '',
            font_color: response.data.font_color ?? '#1A1A2E',
            pal_header_bg: response.data.pal_header_bg,
            pal_text_color: response.data.pal_text_color,
            timezone: response.data.timezone ?? '',
          };
        }
        return { widget_icon_url: '', font_url: '', font_color: '#1A1A2E', timezone: '' };
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error) => [{ type: "Settings", id: "TenantMetadata" }],
    }),
    getTenantTimezones: build.query<TenantTimezone[], void>({
      query: () => apiEndPoints?.tenantTimezones,
      transformResponse: (response: GetTenantTimezonesAPIResponse): TenantTimezone[] =>
        response?.data?.timezones ?? [],
      transformErrorResponse: handleErrorResponse,
    }),
    updateTenantMetadata: build.mutation<TenantMetadataPatchResponse, TenantMetadataPatchPayload>({
      query: (payload) => ({
        url: apiEndPoints?.updateTenantMetadata,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: { data?: TenantMetadataPatchResponse } | TenantMetadataPatchResponse): TenantMetadataPatchResponse => {
        const data = response && typeof response === 'object' && 'data' in response && response.data
          ? response.data
          : (response as TenantMetadataPatchResponse);
        if (data && typeof data === 'object') {
          return {
            widget_icon_url: data.widget_icon_url ?? '',
            font_url: data.font_url ?? '',
            font_color: data.font_color ?? '',
            pal_header_bg: data.pal_header_bg,
            pal_text_color: data.pal_text_color,
            timezone: data.timezone ?? '',
          };
        }
        return { widget_icon_url: '', font_url: '', font_color: '', timezone: '' };
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings", id: "TenantMetadata" }],
    }),
    getTenantMetadataPresignedUrl: build.mutation<TenantMetadataUploadResponse, TenantMetadataUploadRequest>({
      query: (payload) => ({
        url: apiEndPoints?.tenantMetadataUpload,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: GetTenantMetadataUploadAPIResponse | TenantMetadataUploadResponse): TenantMetadataUploadResponse => {
        const data = response && typeof response === 'object' && 'data' in response && response.data
          ? (response as GetTenantMetadataUploadAPIResponse).data
          : (response as TenantMetadataUploadResponse);
        if (data && typeof data === 'object' && typeof data.presigned_url === 'string' && typeof data.widget_icon_url === 'string') {
          return { presigned_url: data.presigned_url, widget_icon_url: data.widget_icon_url };
        }
        return { presigned_url: '', widget_icon_url: '' };
      },
      transformErrorResponse: handleErrorResponse,
    }),
    getGlobalSettings: build.query<SettingsData['settings'], void>({
      query: () => apiEndPoints?.getGlobalSettings,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error) => [{ type: "Settings" }],
    }),
    getData: build.query<any, string>({
      query: (advisor_id) => `${apiEndPoints?.advancedSettings}?advisor_id=${advisor_id}`,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    updateData: build.mutation<any, UpdateDataPayload>({
      query: (payload) => ({
        url: apiEndPoints?.updateData,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    getAdvisorAbilities: build.query<any, { advisor_id: string; }>({
      query: ({ advisor_id }) => `${apiEndPoints?.getAdvisorAbilities}/${advisor_id}`,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    updateSettings: build.mutation<deafultResponseAPIResponse, any>({
      query: (data) => ({
        url: apiEndPoints?.updateSettings,
        method: 'POST',
        body: {
          ...data
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, arg) => (String(arg?.ability ?? '').toLowerCase() === 'rum' ? ['Settings'] : []),
    }),
    getUserProfileDetails: build.query<any, string>({
      query: (userId) => `${apiEndPoints?.getUserProfileDetails}/${userId}`,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    updateUserProfileDetails: build.mutation<deafultResponseAPIResponse, any>({
      query: ({ userID, userData }) => ({
        url: `${apiEndPoints?.getUserProfileDetails}/${userID}`,
        method: 'PATCH',
        body: {
          ...userData
        }
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    updateUserRole: build.mutation<deafultResponseAPIResponse, any>({
      query: ({ userID, roles }) => ({
        url: `${apiEndPoints?.getUserProfileDetails}/${userID}/roles`,
        method: 'PATCH',
        body: {
          roles: roles
        }
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    downloadSSOSAMLFile: build.mutation<BlobPart, any>({
      query: (payLoad) => ({
        url: `${apiEndPoints?.downloadSSOSAMLFile}`,
        method: 'POST',
        body: payLoad
      }),
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    getSSOConnectionDetails: build.query<any, void>({
      query: () => apiEndPoints?.getSSOConnectionDetails,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    createSSOConnction: build.mutation<any, any>({
      query: (payLoad) => ({
        url: `${apiEndPoints?.ssoConnectionPath}`,
        method: 'POST',
        body: payLoad
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    patchSSOConnection: build.mutation<
      any,
      { connectionName: string; group_mappings: string }
    >({
      query: ({ connectionName, group_mappings }) => ({
        url: `${apiEndPoints?.ssoConnectionPath}/${connectionName}`,
        method: 'PATCH',
        body: { group_mappings },
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    deleteSSOConnection: build.mutation<any, string>({
      query: (connectionName) => ({
        url: `${apiEndPoints?.ssoConnectionPath}/${connectionName}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getApiKeysList: build.query<any, void>({
      query: () => apiEndPoints?.apiKeysList,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    getApiKeyDetails: build.query<any, string>({
      query: (api_key_id) => `${apiEndPoints?.apiKeys}?api_type=api_key_id&api_key_id=${api_key_id}`,
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    createApiKey: build.mutation<any, any>({
      query: (payLoad) => ({
        url: `${apiEndPoints?.apiKeys}`,
        method: 'POST',
        body: payLoad
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    deleteApiKey: build.mutation<any, string>({
      query: (apiKeyId) => ({
        url: `${apiEndPoints?.apiKeys}?api_key_id=${apiKeyId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    fetchAdvisorAbilities: build.mutation<any, string>({
      query: (advisor_id) => ({
        url: `${apiEndPoints?.getAdvisorAbilities}/${advisor_id}`,
        method: 'GET'
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    deleteUser: build.mutation<any, string>({
      query: (userId) => ({
        url: `${apiEndPoints?.getTeamMembers}/${userId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    // FGA v2 API Queries
    getFgaAllUsers: build.query<any, void>({
      query: () => apiEndPoints?.getFgaAllUsers,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaUserResources: build.query<any, string>({
      query: (userId) => `${apiEndPoints?.getFgaUserResources}/${userId}/resources`,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaGroups: build.query<any, void>({
      query: () => apiEndPoints?.getFgaGroups,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaGroupNames: build.query<string[], void>({
      query: () => apiEndPoints?.getFgaGroupNames,
      transformResponse: (response: { data?: { group_names?: string[] } }) => response?.data?.group_names ?? [],
      transformErrorResponse: handleErrorResponse
    }),
    getFgaGroupUsers: build.query<any, string>({
      query: (group) => `${apiEndPoints?.getFgaUsersGroup}/${group}/members`,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaAllResources: build.query<any, void>({
      query: () => apiEndPoints?.getFgaAllResources,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaGroupResources: build.query<any, string>({
      query: (group) => `${apiEndPoints?.getFgaGroupResources}/${group}/resources`,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaUserById: build.query<any, string>({
      query: (userId) => `${apiEndPoints?.getFgaAllUsers}/${encodeURIComponent(userId)}`,
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),
    getFgaResourceGroups: build.query<any, { resourceType: 'application_resource' | 'entity_type' | 'entity_resource'; resourceId: string }>({
      query: ({ resourceType, resourceId }) => {
        const encodedId = encodeURIComponent(resourceId);
        if (resourceType === 'application_resource') {
          return `/api/v3/fga/resources/application-resources/${encodedId}/groups`;
        }
        if (resourceType === 'entity_type') {
          return `/api/v3/fga/resources/entity-types/${encodedId}/groups`;
        }
        return `/api/v3/fga/resources/entity-resources/${encodedId}/groups`;
      },
      transformResponse: (response: any) => response,
      transformErrorResponse: handleErrorResponse
    }),

    // FGA v2 API Mutations
    addFgaUser: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.addFgaUser}/${payload.group}/members`,
        method: 'POST',
        body: { user_id: payload.user_id }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    removeFgaUser: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.removeFgaUser}/${payload.group}/members/${payload.user_id}`,
        method: 'DELETE'
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    deleteAuth0User: build.mutation<deafultResponseAPIResponse, string>({
      query: (userId) => ({
        url: `${apiEndPoints.deleteAuth0User}/${encodeURIComponent(userId.trim())}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    addFgaGroup: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: apiEndPoints?.addFgaGroup,
        method: 'POST',
        body: { group: payload.group }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    deleteFgaGroup: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.deleteFgaGroup}/${payload.group}`,
        method: 'DELETE'
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    addFgaEntityResource: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.addFgaResource}/${payload.group}/resources/entity-resources`,
        method: 'POST',
        body: {
          entity_resource: payload.entity_resource,
          relation: payload.relation
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    removeFgaEntityResource: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.removeFgaResource}/${payload.group}/resources/entity-resources`,
        method: 'DELETE',
        body: {
          entity_resource: payload.entity_resource,
          relation: payload.relation
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    addFgaApplicationResource: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.addFgaResource}/${payload.group}/resources/application-resources`,
        method: 'POST',
        body: {
          application_resource: payload.application_resource,
          relation: payload.relation
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    removeFgaApplicationResource: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.removeFgaResource}/${payload.group}/resources/application-resources`,
        method: 'DELETE',
        body: {
          application_resource: payload.application_resource,
          relation: payload.relation
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    addFgaEntityType: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.addFgaResource}/${payload.group}/resources/entity-types`,
        method: 'POST',
        body: {
          entity_type: payload.entity_type,
          relation: payload.relation
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    removeFgaEntityType: build.mutation<deafultResponseAPIResponse, any>({
      query: (payload) => ({
        url: `${apiEndPoints?.removeFgaResource}/${payload.group}/resources/entity-types`,
        method: 'DELETE',
        body: {
          entity_type: payload.entity_type,
          relation: payload.relation
        }
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    fgaInvite: build.mutation<any, { first_name: string; last_name: string; mail_id: string; groups: string[]; user_metadata?: Record<string, string> }>({
      query: (payload) => ({
        url: apiEndPoints?.fgaInvite,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }],
    }),
    updateUserMetadata: build.mutation<deafultResponseAPIResponse, { user_id: string; user_metadata: Record<string, string | null> }>({
      query: ({ user_id, user_metadata }) => ({
        url: `${apiEndPoints?.updateUserMetadata}/${user_id}/user-metadata`,
        method: 'PATCH',
        body: { user_metadata },
      }),
      transformErrorResponse: handleErrorResponse,
    }),

    // Scheduler API endpoints
    getSchedules: build.query<Schedule[], void>({
      query: () => apiEndPoints?.getSchedules,
      transformResponse: (response: any) => {
        // Handle the actual API response structure: response.data.items
        if (response?.data?.items && Array.isArray(response.data.items)) {
          return response.data.items;
        }
        // Fallback: check if data is directly an array
        const data = response?.data || response;
        if (Array.isArray(data)) {
          return data;
        }
        // If data is an object with a schedules property, extract it
        if (data && typeof data === 'object' && Array.isArray(data.schedules)) {
          return data.schedules;
        }
        // If data is an object with a list property, extract it
        if (data && typeof data === 'object' && Array.isArray(data.list)) {
          return data.list;
        }
        // If data is an object with an items property, extract it
        if (data && typeof data === 'object' && Array.isArray(data.items)) {
          return data.items;
        }
        // Default to empty array
        return [];
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error) => [{ type: "Settings" }],
    }),
    getScheduleDetails: build.query<Schedule, string>({
      query: (schedulerId) => `${apiEndPoints?.getScheduleDetails}/${schedulerId}`,
      transformResponse: (response: any) => {
        // Handle nested response structure: response.data might contain the schedule object
        if (response?.data) {
          // If data is an object with scheduler_id, it's the schedule itself
          if (response.data.scheduler_id) {
            return response.data;
          }
          // If data has items array (unlikely for single item, but handle it)
          if (Array.isArray(response.data.items) && response.data.items.length > 0) {
            return response.data.items[0];
          }
        }
        // Return response directly if it has scheduler_id
        if (response?.scheduler_id) {
          return response;
        }
        return response?.data || response;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, schedulerId) => [
        { type: "Settings", id: `Schedule-${schedulerId}` }
      ],
    }),
    createSchedule: build.mutation<deafultResponseAPIResponse, CreateSchedulePayload>({
      query: (payload) => ({
        url: apiEndPoints?.createSchedule,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings" }]
    }),
    updateSchedule: build.mutation<deafultResponseAPIResponse, { schedulerId: string } & UpdateSchedulePayload>({
      query: ({ schedulerId, ...payload }) => ({
        url: `${apiEndPoints?.updateSchedule}/${schedulerId}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, { schedulerId }) => [
        { type: "Settings" },
        { type: "Settings", id: `Schedule-${schedulerId}` }
      ]
    }),
    deleteSchedule: build.mutation<deafultResponseAPIResponse, string>({
      query: (schedulerId) => ({
        url: `${apiEndPoints?.deleteSchedule}/${schedulerId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, schedulerId) => [
        { type: "Settings" },
        { type: "Settings", id: `Schedule-${schedulerId}` }
      ]
    }),
    runSchedule: build.mutation<deafultResponseAPIResponse, string>({
      query: (schedulerId) => ({
        url: `${apiEndPoints?.runSchedule}/${schedulerId}/run`,
        method: 'POST',
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, schedulerId) => [
        { type: "Settings" },
        { type: "Settings", id: `Schedule-${schedulerId}` },
        { type: "Settings", id: `SchedulerHistory-${schedulerId}` },
      ],
    }),

    getLambdaFunctions: build.query<any[], void>({
      query: () => '/api/v1/aws_resources/lambda_functions',
      transformResponse: (response: any) => response?.data?.functions || [],
      transformErrorResponse: handleErrorResponse,
    }),
    getStepFunctions: build.query<any[], void>({
      query: () => '/api/v1/aws_resources/step_functions',
      transformResponse: (response: any) => response?.data?.state_machines || [],
      transformErrorResponse: handleErrorResponse,
    }),
    getFabricPipelines: build.query<any[], void>({
      query: () => '/api/v1/azure_resources/fabric_pipelines',
      transformResponse: (response: any) => response?.data?.pipelines || [],
      transformErrorResponse: handleErrorResponse,
    }),

    getSchedulerHistory: build.query<
      SchedulerHistoryResponse,
      { schedulerId: string; page?: number; per_page?: number }
    >({
      query: ({ schedulerId, page = 1, per_page = 20 }) => ({
        url: `/api/v1/scheduler_history/scheduler/${schedulerId}`,
        params: { page, per_page },
      }),
      transformResponse: (response: any): SchedulerHistoryResponse => {
        const raw = response?.data ?? response;
        // Backend may return batches as the payload object { data, total_count, ... } or as a bare array under .data
        if (Array.isArray(raw)) {
          return {
            data: raw,
            total_count:
              typeof response?.total_count === 'number' ? response.total_count : raw.length,
            page: typeof response?.page === 'number' ? response.page : 1,
            per_page: typeof response?.per_page === 'number' ? response.per_page : raw.length,
            scheduler_id: typeof response?.scheduler_id === 'string' ? response.scheduler_id : '',
          };
        }
        if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
          return raw as SchedulerHistoryResponse;
        }
        return {
          data: [],
          total_count: 0,
          page: 1,
          per_page: 20,
          scheduler_id: '',
        };
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, { schedulerId }) => [
        { type: "Settings", id: `SchedulerHistory-${schedulerId}` }
      ],
    }),

    getSchedulerBatchDetail: build.query<SchedulerBatchDetailResponse, string>({
      query: (batchId) => `${apiEndPoints?.getSchedulerBatchDetail}/${batchId}`,
      transformResponse: (response: any) => {
        const d = response?.data;
        if (d && typeof d === 'object') return d;
        return response;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, batchId) => [
        { type: "Settings", id: `SchedulerBatch-${batchId}` }
      ],
    }),

    // Credentials API endpoints
    getCredentialsList: build.query<Credential[], void>({
      query: () => apiEndPoints?.credentialsList,
      transformResponse: (response: { data: { credentials: Credential[] } }) => {
        return response?.data?.credentials || response?.data || response.data.credentials;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: [{ type: "Settings", id: "Credentials" }],
    }),
    getCredentialById: build.query<Credential, string>({
      query: (credential_id) => `${apiEndPoints?.credentials}/${credential_id}`,
      transformResponse: (response: { data: { credential: Credential } }) => {
        return response?.data?.credential || response?.data;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, credential_id) => [{ type: "Settings", id: `Credential-${credential_id}` }],
    }),
    createCredential: build.mutation<deafultResponseAPIResponse, CredentialPayload>({
      query: (payload) => ({
        url: apiEndPoints?.credentials,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings", id: "Credentials" }],
    }),
    updateCredential: build.mutation<deafultResponseAPIResponse, { credential_id: string; payload: CredentialPayload }>({
      query: ({ credential_id, payload }) => ({
        url: `${apiEndPoints?.credentials}/${credential_id}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, { credential_id }) => [
        { type: "Settings", id: "Credentials" },
        { type: "Settings", id: `Credential-${credential_id}` }
      ],
    }),
    testCredential: build.query<deafultResponseAPIResponse, string>({
      query: (credential_id) => ({
        url: `${apiEndPoints?.credentials}/${credential_id}/test`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
    }),
    deleteCredential: build.mutation<deafultResponseAPIResponse, string>({
      query: (credential_id) => ({
        url: `${apiEndPoints?.credentials}/${credential_id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings", id: "Credentials" }],
    }),

    getDataConnectors: build.query<DataConnectorsListPayload, GetDataConnectorsQueryArg | void>({
      query: (arg) => {
        const params: Record<string, string> = {};
        if (arg && typeof arg === 'object') {
          const search = arg.search?.trim();
          if (search) params.search = search;
          if (arg.status === 'active' || arg.status === 'inactive') {
            params.status = arg.status;
          }
        }
        return {
          url: apiEndPoints?.dataConnectors,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        };
      },
      transformResponse: (response: { data?: DataConnectorsListPayload }): DataConnectorsListPayload => {
        const d = response?.data;
        if (d && typeof d === 'object' && Array.isArray(d.connectors)) {
          return d;
        }
        return {
          connectors: [],
          total_connections: 0,
          active_connections: 0,
          inactive_connections: 0,
          total_all_connections: 0,
        };
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: [{ type: "Settings", id: "DataConnectors" }],
    }),
    getDataConnectorById: build.query<DataConnector, string>({
      query: (connector_id) => `${apiEndPoints?.dataConnectors}/${connector_id}`,
      transformResponse: (response: { data?: DataConnector | { connector?: DataConnector } }): DataConnector => {
        const d = response?.data;
        if (d && typeof d === 'object' && 'connector_id' in d) {
          return d;
        }
        if (d && typeof d === 'object' && 'connector' in d && (d as { connector: DataConnector }).connector) {
          return (d as { connector: DataConnector }).connector;
        }
        return d as DataConnector;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (result, error, connector_id) => [{ type: "Settings", id: `DataConnector-${connector_id}` }],
    }),
    createDataConnector: build.mutation<deafultResponseAPIResponse, CreateDataConnectorPayload>({
      query: (body) => ({
        url: apiEndPoints?.dataConnectors,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings", id: "DataConnectors" }],
    }),
    updateDataConnector: build.mutation<
      deafultResponseAPIResponse,
      { connector_id: string; payload: UpdateDataConnectorPayload }
    >({
      query: ({ connector_id, payload }) => ({
        url: `${apiEndPoints?.dataConnectors}/${connector_id}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (result, error, { connector_id }) => [
        { type: "Settings", id: "DataConnectors" },
        { type: "Settings", id: `DataConnector-${connector_id}` },
      ],
    }),
    deleteDataConnector: build.mutation<deafultResponseAPIResponse, string>({
      query: (connector_id) => ({
        url: `${apiEndPoints?.dataConnectors}/${connector_id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: any) => response?.data || response,
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: "Settings", id: "DataConnectors" }],
    }),
    getAuditLogs: build.query<AuditLogsData, AuditLogsHistoryRequest>({
      query: (body) => ({
        url: apiEndPoints?.auditLogsHistory,
        method: 'POST',
        body,
      }),
      transformResponse: (response: AuditLogsAPIResponse) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    getAuditLogById: build.query<AuditLogDetail, string>({
      query: (auditId) => `${apiEndPoints?.auditLogs}/${auditId}`,
      transformResponse: (response: AuditLogDetailAPIResponse) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    reprocessClaims: build.mutation<ReprocessClaimsAPIResponse, ReprocessClaimsPayload>({
      query: (payload) => ({
        url: apiEndPoints?.reprocessClaims,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
  })
});


export const {
  useInviteUserMutation,
  useGetTeamMembersQuery,
  useGetDetailsQuery,
  useUpdateTenantDetailsMutation,
  useGetTenantMetadataQuery,
  useGetTenantTimezonesQuery,
  useUpdateTenantMetadataMutation,
  useGetTenantMetadataPresignedUrlMutation,
  useGetGlobalSettingsQuery,
  useGetAdvisorAbilitiesQuery,
  useUpdateSettingsMutation,
  useGetUserProfileDetailsQuery,
  useUpdateUserProfileDetailsMutation,
  useUpdateUserRoleMutation,
  useDownloadSSOSAMLFileMutation,
  useGetSSOConnectionDetailsQuery,
  useCreateSSOConnctionMutation,
  usePatchSSOConnectionMutation,
  useDeleteSSOConnectionMutation,
  useGetApiKeysListQuery,
  useGetApiKeyDetailsQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useFetchAdvisorAbilitiesMutation,
  useDeleteUserMutation,
  useGetDataQuery,
  useUpdateDataMutation,
  // FGA v2 API Hooks
  useGetFgaAllUsersQuery,
  useGetFgaUserResourcesQuery,
  useGetFgaGroupsQuery,
  useGetFgaGroupNamesQuery,
  useGetFgaGroupUsersQuery,
  useLazyGetFgaGroupUsersQuery,
  useGetFgaAllResourcesQuery,
  useGetFgaGroupResourcesQuery,
  useGetFgaUserByIdQuery,
  useLazyGetFgaUserByIdQuery,
  useGetFgaResourceGroupsQuery,
  useAddFgaUserMutation,
  useRemoveFgaUserMutation,
  useDeleteAuth0UserMutation,
  useAddFgaGroupMutation,
  useDeleteFgaGroupMutation,
  useAddFgaEntityResourceMutation,
  useRemoveFgaEntityResourceMutation,
  useAddFgaApplicationResourceMutation,
  useRemoveFgaApplicationResourceMutation,
  useAddFgaEntityTypeMutation,
  useRemoveFgaEntityTypeMutation,
  useFgaInviteMutation,
  useUpdateUserMetadataMutation,
  // Scheduler API Hooks
  useGetSchedulesQuery,
  useGetLambdaFunctionsQuery,
  useGetStepFunctionsQuery,
  useGetFabricPipelinesQuery,
  useGetScheduleDetailsQuery,
  useGetSchedulerHistoryQuery,
  useGetSchedulerBatchDetailQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  useRunScheduleMutation,
  // Credentials API Hooks
  useGetCredentialsListQuery,
  useGetCredentialByIdQuery,
  useCreateCredentialMutation,
  useUpdateCredentialMutation,
  useLazyTestCredentialQuery,
  useDeleteCredentialMutation,
  useGetDataConnectorsQuery,
  useLazyGetDataConnectorByIdQuery,
  useCreateDataConnectorMutation,
  useUpdateDataConnectorMutation,
  useDeleteDataConnectorMutation,
  useGetAuditLogsQuery,
  useLazyGetAuditLogsQuery,
  useGetAuditLogByIdQuery,
  useLazyGetAuditLogByIdQuery,
  useReprocessClaimsMutation,
} = settingsApiSlice;